import { useEffect, useState, useMemo, useRef } from "react";
import { Container, Navbar, Row, Card, Button } from 'react-bootstrap'
import * as label from '../../shared/util/label'
import { ErrorBoundary } from 'react-error-boundary'
import { Column, DateTimePicker, Select } from '../../components';
import Logger from '../../shared/services/Logger';
import { UserStatsResponse } from '../model/UserStatsResponse';
import Grid from '../../components/components/Grid/Grid';
import StatisticsDataService from './StatisticsDataService';
import ExportToCsv from '../../shared/components/ExportToCSV/ExportToCsv';
import { CommonUtility } from '../../Services/CommonUtility';
import './statisticsDashboard.scss';

interface IStatsGridState {
    stats: any[],
    gridColumns: any[]
}

var sampleGridData: UserStatsResponse[] = [];

const initialStatsGridState: IStatsGridState = {
    stats: sampleGridData,
    gridColumns: []
}

const StatisticsDashboard = () => {

    const [selectedFromDate, setSelectedFromDate] = useState(new Date());
    const [selectedToDate, setSelectedToDate] = useState(new Date());
    const [isFromDateInvalid, setIsFromDateInvalid] = useState(false);
    const [isToDateInvalid, setIsToDateInvalid] = useState(false);
    const [fromDateValidError, setFromDateValidError] = useState(null);
    const [toDateValidError, setToDateValidError] = useState(null);
    const [statsGridState, setStatsGridState] = useState({ stats: [], gridColumns: [] });
    const { stats, gridColumns } = statsGridState;
    const statisticsDataServiceeObj = useMemo(() => new StatisticsDataService(), []);
    const gridRef = useRef<any>(null);

    useEffect(() => {
        initializeGrid();
    }, [statisticsDataServiceeObj]);


    const initializeGrid = () => {
        const columns = statisticsDataServiceeObj.getGridColumns();
        setStatsGridState(prev => ({ ...prev, gridColumns: columns, stats: [] }));
    }

    const validateDates = (fromDate, toDate) => {
        if (fromDate && toDate) {
            if (fromDate <= toDate) {
                setIsFromDateInvalid(false);
                setFromDateValidError("");
            } else {
                setIsFromDateInvalid(true);
                setFromDateValidError("From Date must be greater than To Date");
            }

            if (toDate >= fromDate) {
                setIsToDateInvalid(false);
                setToDateValidError("");
            } else {
                setIsToDateInvalid(true);
                setToDateValidError("To Date must be less than From Date");
            }
        }
    };

    const handleFromDateChange = date => {
        setSelectedFromDate(date);
        validateDates(date, selectedToDate);
    };

    const handleToDateChange = date => {
        setSelectedToDate(date);
        validateDates(selectedFromDate, date);
    };

    const isButtonEnabled = selectedFromDate && selectedToDate;

    const handleSearchClick = async () => {
        try {
            setStatsGridState(prev => ({ ...prev, stats: [] }));            

            const { stats, columns } = await statisticsDataServiceeObj.getStatsGridColumns(selectedFromDate.toString(), selectedToDate.toString());
            setStatsGridState({ stats, gridColumns: columns });
        }
        catch (error) {
            Logger.logError(error.toString());
        }
    }

    const handleExportData = async () => {
        try {
            // Get filtered rows from Grid
            const selectedRows = gridRef.current?.getSelectedRows() || [];
            const filteredData = selectedRows.length > 0 
                ? selectedRows.map(row => row.original)
                : stats;

            // Map data to export format
            return filteredData.map(stat => {
                const exportRow = {};
                gridColumns.forEach(col => {
                    if (col.accessor && col.Header) {
                        exportRow[col.Header] = stat[col.accessor] || '';
                    }
                });
                return exportRow;
            });
        } catch (error) {
            Logger.logError('Export error: ' + error.toString());
            return [];
        }
    };

    const FallbackComponent = ({ error, resetErrorBoundary }) => {
        return (
            <div>
                <h1>An error occurred: {error.message}</h1>
                <button onClick={resetErrorBoundary}>Try again</button>
            </div>
        );
    };

    return (
        <ErrorBoundary FallbackComponent={FallbackComponent}>
            <Container fluid>
                <Navbar className="bg-transparent">
                    <Navbar.Brand>{label.Statistics.STATISTICS_HEADER}</Navbar.Brand>
                </Navbar>
                <Column lg={12}>
                    <Row className='mb-4'>
                        <Column md={3}>
                            <DateTimePicker
                                tabIndex={1}
                                selected={selectedFromDate}
                                onChange={handleFromDateChange}
                                className={`statsDatePicker ${isFromDateInvalid ? "is-invalid" : ""}`}
                                minTime={new Date(1990, 0, 1)}
                                maxDate={new Date()}
                                placeholderText={label.Statistics.FROM_DATE}
                                isInvalid={isFromDateInvalid == true}
                                invalidWarning={fromDateValidError ? fromDateValidError : ''}
                            />
                        </Column>
                        <Column md={3}>
                            <DateTimePicker
                                tabIndex={2}
                                selected={selectedToDate}
                                onChange={handleToDateChange}
                                className={`statsDatePicker ${isToDateInvalid ? "is-invalid" : ""}`}
                                minTime={new Date(1990, 0, 1)}
                                maxDate={new Date()}
                                isInvalid={isToDateInvalid == true}
                                invalidWarning={toDateValidError ? toDateValidError : ''}
                                placeholderText={label.Statistics.TO_DATE}
                            />
                        </Column>
                        <Column md={3}>
                            <Button variant="primary" key="filter"
                                onClick={handleSearchClick}
                                tabIndex={3}
                                id="btnStatsSearch"
                                disabled={!isButtonEnabled}
                            >
                                {label.Statistics.GET_RESULTS}
                            </Button>
                        {/* </Column>
                        <Column md={3}> */}
                            <ExportToCsv
                                onExport={handleExportData}
                                headers={gridColumns.filter(col => col.accessor && col.Header).map(col => col.Header)}
                                fileName={`Statistics_${CommonUtility.formateDate(selectedFromDate, "MMDDYYYY")}_${CommonUtility.formateDate(selectedToDate, "MMDDYYYY")}.csv`}
                                buttonLabel="EXPORT"
                                className="btn btn-primary export-btn-margin"
                                disabled={stats.length === 0}
                            />
                        </Column>
                    </Row>
                    <Row>
                        <div className="">
                            <Card className='p-0'>
                                <Grid
                                    ref={gridRef}
                                    data={stats}
                                    showFilters={true}
                                    id={'statsGrid'}
                                    retainGridState={true}
                                    columns={gridColumns}
                                    hiddenColumns={['selection']}
                                    sortDefault={[]}
                                    grdTableContainerClass="p-0 table-container-campaign"
                                    grdTabelClass="grd-table table-striped table-hover table-sm"
                                    zindexHeader={10000}
                                />
                            </Card>
                        </div>
                    </Row>
                </Column>
            </Container>
        </ErrorBoundary>
    );
};

export default StatisticsDashboard;
